// Description: Global state management (Domain + UI only)
// Updated: 03/2026 – Auth fully removed from Zustand
// Author: Simon Franklin

import { create } from "zustand";
import { getProfile } from "../api/profile/getProfile";
import { getProfileEntitlements } from "../api/profile/getProfileEntitlements";
import AlternativeAvatar from "../shared/assets/avatars/default-avatar.png";
import {
  parseFeatureAccessFromEntitlements,
} from "../shared/lib/access/featureAccess";

const PLAN_STORAGE_KEY = "app:planoAtual";
const FEATURE_ACCESS_STORAGE_KEY = "app:featureAccess";
const FEATURE_ACCESS_LOCAL_STORAGE_PREFIX = "app:featureAccess:account:";
const FEATURE_ACCESS_STORAGE_VERSION = "modules-permissions-v2";
const FEATURE_ACCESS_CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000;

let featureAccessLoadPromise = null;
let featureAccessLoadAccountId = "";

const initialFeatureAccessState = getInitialFeatureAccessState();
const initialFeatureAccess = initialFeatureAccessState.access;
const initialFeatureAccessAccountId = initialFeatureAccessState.accountId;
const initialPlan = getInitialPlan();

function getInitialPlan() {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(PLAN_STORAGE_KEY);
}

function persistPlan(plan) {
  if (typeof window === "undefined") return;

  if (plan) {
    sessionStorage.setItem(PLAN_STORAGE_KEY, plan);
    return;
  }

  sessionStorage.removeItem(PLAN_STORAGE_KEY);
}

function getFeatureAccessLocalStorageKey(accountId) {
  const normalizedAccountId = String(accountId || "").trim();
  return normalizedAccountId
    ? `${FEATURE_ACCESS_LOCAL_STORAGE_PREFIX}${normalizedAccountId}`
    : "";
}

function parseStoredFeatureAccess(raw, options = {}) {
  const { accountId = "", requireFresh = false } = options;
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const parsedAccountId = String(parsed?.accountId || "").trim();
    const normalizedAccountId = String(accountId || "").trim();
    const cachedAt = Number(parsed?.cachedAt || 0);

    if (
      parsed?.contract !== FEATURE_ACCESS_STORAGE_VERSION ||
      !parsedAccountId ||
      !parsed.access ||
      typeof parsed.access !== "object" ||
      Array.isArray(parsed.access)
    ) {
      return null;
    }

    if (normalizedAccountId && parsedAccountId !== normalizedAccountId) {
      return null;
    }

    if (
      requireFresh &&
      (!cachedAt || Date.now() - cachedAt > FEATURE_ACCESS_CACHE_MAX_AGE_MS)
    ) {
      return null;
    }

    return { access: parsed.access, accountId: parsedAccountId, cachedAt };
  } catch (err) {
    console.warn("Não foi possível recuperar feature access do storage:", err);
    return null;
  }
}

function getInitialFeatureAccessState() {
  const emptyState = { access: null, accountId: "" };
  if (typeof window === "undefined") return emptyState;
  const parsed = parseStoredFeatureAccess(
    sessionStorage.getItem(FEATURE_ACCESS_STORAGE_KEY),
  );

  if (parsed) {
    return { access: parsed.access, accountId: parsed.accountId };
  }

  return emptyState;
}

function getCachedFeatureAccessForAccount(accountId) {
  if (typeof window === "undefined") return null;
  const key = getFeatureAccessLocalStorageKey(accountId);
  if (!key) return null;

  return parseStoredFeatureAccess(window.localStorage.getItem(key), {
    accountId,
    requireFresh: true,
  });
}

function clearStoredFeatureAccess(accountId = "") {
  if (typeof window === "undefined") return;
  const normalizedAccountId = String(accountId || "").trim();

  sessionStorage.removeItem(FEATURE_ACCESS_STORAGE_KEY);

  if (normalizedAccountId) {
    window.localStorage.removeItem(
      getFeatureAccessLocalStorageKey(normalizedAccountId),
    );
    return;
  }

  const keysToRemove = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(FEATURE_ACCESS_LOCAL_STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
}

function persistFeatureAccess(featureAccess, accountId = "") {
  if (typeof window === "undefined") return;
  const normalizedAccountId = String(accountId || "").trim();

  if (featureAccess && typeof featureAccess === "object" && normalizedAccountId) {
    const payload = JSON.stringify({
      accountId: normalizedAccountId,
      access: featureAccess,
      cachedAt: Date.now(),
      contract: FEATURE_ACCESS_STORAGE_VERSION,
    });

    sessionStorage.setItem(FEATURE_ACCESS_STORAGE_KEY, payload);
    window.localStorage.setItem(
      getFeatureAccessLocalStorageKey(normalizedAccountId),
      payload,
    );
    return;
  }

  clearStoredFeatureAccess(normalizedAccountId);
}

const use123zapStore = create((set, get) => ({
  // =====================================================
  // DOMAIN STATE
  // =====================================================

  templates: [],
  setTemplates: (value) => set({ templates: value }),
  alreadyFetchTemplates: false,
  setAlreadyFetchTemplates: (value) => set({ alreadyFetchTemplates: value }),

  channels: [],
  setChannels: (value) => set({ channels: value }),
  channelLimits: {},
  setChannelLimits: (value) => set({ channelLimits: value || {} }),
  alreadyFetchChannels: false,
  setAlreadyFetchChannels: (value) => set({ alreadyFetchChannels: value }),

  campaigns: [],
  setCampaigns: (value) => set({ campaigns: value }),
  alreadyFetchCampaigns: false,
  setAlreadyFetchCampaigns: (value) => set({ alreadyFetchCampaigns: value }),

  sheets: [],
  setSheets: (value) => set({ sheets: value }),
  alreadyFetchSheets: false,
  setAlreadyFetchSheets: (value) => set({ alreadyFetchSheets: value }),

  integrations: [],
  setIntegrations: (value) => set({ integrations: value }),
  alreadyFetchIntegrations: false,
  setAlreadyFetchIntegrations: (value) =>
    set({ alreadyFetchIntegrations: value }),

  registeredERPS: [],
  setRegisteredERPS: (value) => set({ registeredERPS: value }),
  alreadyFetchRegisteredERPS: false,
  setAlreadyFetchRegisteredERPS: (value) =>
    set({ alreadyFetchRegisteredERPS: value }),

  planoAtual: initialPlan,
  setPlanoAtual: (value) => {
    persistPlan(value);
    set({ planoAtual: value });
  },
  clearPlanoAtual: () => {
    persistPlan(null);
    set({ planoAtual: null });
  },
  featureAccess: initialFeatureAccess,
  featureAccessAccountId: initialFeatureAccessAccountId,
  featureAccessLoaded: Boolean(initialFeatureAccess),
  featureAccessLoading: false,
  accountStatus: null,
  setFeatureAccess: (value, accountId = "") => {
    persistFeatureAccess(value, accountId);
    set({
      featureAccess: value,
      featureAccessAccountId: String(accountId || "").trim(),
      featureAccessLoaded: true,
      featureAccessLoading: false,
    });
  },
  clearFeatureAccess: () => {
    persistFeatureAccess(null);
    set({
      featureAccess: null,
      featureAccessAccountId: "",
      featureAccessLoaded: false,
      featureAccessLoading: false,
    });
  },
  hydrateFeatureAccessFromCache: (accountId = "") => {
    const normalizedAccountId = String(accountId || "").trim();
    const cached = getCachedFeatureAccessForAccount(normalizedAccountId);

    if (!cached) {
      return false;
    }

    persistFeatureAccess(cached.access, normalizedAccountId);
    set({
      featureAccess: cached.access,
      featureAccessAccountId: normalizedAccountId,
      featureAccessLoaded: true,
      featureAccessLoading: false,
    });

    return true;
  },

  // =====================================================
  // PROFILE (não é auth, é domínio)
  // =====================================================

  userAvatar: AlternativeAvatar,
  setUserAvatar: (value) => set({ userAvatar: value }),

  loadPlanoAtual: async (force = false, currentAccountId = "", options = {}) => {
    const { featureAccess, featureAccessAccountId, planoAtual } = get();
    const normalizedCurrentAccountId = String(currentAccountId || "").trim();
    const includeProfile = Boolean(options?.includeProfile);
    const silent = Boolean(options?.silent);
    const hasAccessForCurrentAccount =
      featureAccess &&
      featureAccessAccountId &&
      (!normalizedCurrentAccountId || featureAccessAccountId === normalizedCurrentAccountId);

    if (force || !planoAtual || !hasAccessForCurrentAccount) {
      if (
        featureAccessLoadPromise &&
        featureAccessLoadAccountId === normalizedCurrentAccountId
      ) {
        return featureAccessLoadPromise;
      }

      featureAccessLoadAccountId = normalizedCurrentAccountId;
      featureAccessLoadPromise = (async () => {
        try {
          if (!silent) {
            set({ featureAccessLoading: true });
          }

          const [response, entitlementsResponse] = await Promise.all([
            includeProfile ? getProfile().catch(() => null) : Promise.resolve(null),
            getProfileEntitlements({ scope: "access" }),
          ]);
          const entitlements = entitlementsResponse?.entitlements || null;
          const parsedFeatureAccess = parseFeatureAccessFromEntitlements(
            entitlements,
          );
          const accountId = String(
            entitlements?.account_id ||
            response?.account_id ||
            response?.id ||
            normalizedCurrentAccountId
          ).trim();
          const planName =
            response?.plan_name ||
            entitlements?.plan?.name ||
            entitlements?.plans?.[0]?.name ||
            planoAtual ||
            null;
          const accountStatus =
            response?.account_status || response?.accountStatus || null;

          persistPlan(planName);
          persistFeatureAccess(parsedFeatureAccess, accountId);
          set({
            accountStatus,
            featureAccessLoaded: true,
            featureAccessLoading: false,
            featureAccessAccountId: accountId,
            planoAtual: planName,
            featureAccess: parsedFeatureAccess,
          });
          return {
            entitlements,
            profile: response,
          };
        } catch (err) {
          const currentState = get();
          const canKeepCachedAccess =
            silent &&
            currentState.featureAccess &&
            currentState.featureAccessAccountId === normalizedCurrentAccountId;

          if (!canKeepCachedAccess) {
            persistFeatureAccess(null, normalizedCurrentAccountId);
          }

          set({
            accountStatus: canKeepCachedAccess
              ? currentState.accountStatus
              : null,
            featureAccess: canKeepCachedAccess
              ? currentState.featureAccess
              : null,
            featureAccessAccountId: normalizedCurrentAccountId,
            featureAccessLoaded: true,
            featureAccessLoading: false,
          });
          console.error("Erro ao carregar plano atual:", err);
          return null;
        } finally {
          featureAccessLoadPromise = null;
          featureAccessLoadAccountId = "";
        }
      })();

      return featureAccessLoadPromise;
    }

    return null;
  },

  // =====================================================
  // DASHBOARD STATE
  // =====================================================

  dashboardSentiments: null,
  dashboardTotalMessages: 0,
  dashboardLoading: false,
  dashboardLoaded: false,

  setDashboardData: (sentiments, total) =>
    set({
      dashboardSentiments: sentiments,
      dashboardTotalMessages: total,
      dashboardLoaded: true,
    }),

  setDashboardLoading: (value) => set({ dashboardLoading: value }),

  resetDashboard: () =>
    set({
      dashboardLoaded: false,
    }),

  // =====================================================
  // AI ANALYSIS STATE
  // =====================================================

  aiAnalysisText: "",
  aiAnalysisLoading: false,
  aiAnalysisGenerated: false,

  setAiAnalysis: (text) =>
    set({
      aiAnalysisText: text,
      aiAnalysisGenerated: true,
    }),

  setAiAnalysisLoading: (value) => set({ aiAnalysisLoading: value }),

  resetAiAnalysis: () =>
    set({
      aiAnalysisText: "",
      aiAnalysisGenerated: false,
    }),

  // =====================================================
  // UI STATE
  // =====================================================

  finishedRegister: null,
  setFinishedRegister: (value) => set({ finishedRegister: value }),

  isCollapsed: false,
  setIsCollapsed: (value) => set({ isCollapsed: value }),

  showSidebar: false,
  setShowSidebar: (value) => set({ showSidebar: value }),

  // =====================================================
  // TABLE / REPORT STATE
  // =====================================================

  campaignsSummaryData: [],
  setCampaignsSummaryData: (value) => set({ campaignsSummaryData: value }),

  campaignReportRows: [],
  setCampaignReportRows: (value) => set({ campaignReportRows: value }),

  showNewTable: false,
  setShowNewTable: (value) => set({ showNewTable: value }),

  modalData: { label: "", date: "" },
  setModalData: (value) => set({ modalData: value }),

  resetSessionState: () => {
    persistPlan(null);
    persistFeatureAccess(null);

    set({
      aiAnalysisGenerated: false,
      aiAnalysisLoading: false,
      aiAnalysisText: "",
      alreadyFetchCampaigns: false,
      alreadyFetchChannels: false,
      alreadyFetchIntegrations: false,
      alreadyFetchRegisteredERPS: false,
      alreadyFetchSheets: false,
      alreadyFetchTemplates: false,
      campaignReportRows: [],
      campaigns: [],
      campaignsSummaryData: [],
      channelLimits: {},
      channels: [],
      dashboardLoaded: false,
      dashboardLoading: false,
      dashboardSentiments: null,
      dashboardTotalMessages: 0,
      featureAccess: null,
      featureAccessAccountId: "",
      featureAccessLoaded: false,
      featureAccessLoading: false,
      finishedRegister: null,
      accountStatus: null,
      integrations: [],
      modalData: { label: "", date: "" },
      planoAtual: null,
      registeredERPS: [],
      sheets: [],
      showNewTable: false,
      showSidebar: false,
      templates: [],
      userAvatar: AlternativeAvatar,
    });
  },
}));

export default use123zapStore;
